import { FetchResult } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormErrorMessage } from '@pendwa/common-browser/components/Form/ErrorMessage/index.js';
import { FormInputPhoneNumber } from '@pendwa/common-browser/components/Form/InputPhoneNumber/index.js';
import { FormSubmitButton } from '@pendwa/common-browser/components/Form/SubmitButton/index.js';
import { LayoutSpacer } from '@pendwa/common-browser/components/LayoutSpacer/index.js';
import { LayoutSpacerSmall } from '@pendwa/common-browser/components/LayoutSpacerSmall/index.js';
import { LoadingPage } from '@pendwa/common-browser/components/Loading/LoadingPage/index.js';
import { ContextPageData } from '@pendwa/common-browser/context/PageData/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import { userSchema } from '@pendwa/db/generated/zod/index.js';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { HooksAccountLoginCodeMutation } from '../../../generated/graphql.js';
import { useSendLoginCodeGraphql } from '../../../hooks/user/sendLoginCode.js';
import { LoginCode } from '../LoginCode/index.js';

const schema = z
  .object({
    phone_number: userSchema.shape.phone_number,
  })
  .required();

export const SendLoginCodePhoneNumber: React.FC<{
  handleSuccess: (result: FetchResult<HooksAccountLoginCodeMutation>) => void;
}> = (props) => {
  const { result: mutation_result, doAction } = useSendLoginCodeGraphql();
  const { country } = React.useContext(ContextPageData);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  if (country?.loading && !country.data) {
    return <LoadingPage />;
  }

  if (mutation_result.data?.sendLoginCode.message) {
    return (
      <LoginCode
        handleSuccess={props.handleSuccess}
        message={mutation_result.data.sendLoginCode.message}
        backToSendCode={() => mutation_result.reset()}
      />
    );
  }

  return (
    <LayoutSpacer>
      {mutation_result.error && (
        <FormErrorMessage
          message={extractGraphQLErrorMessage(mutation_result.error) ?? 'Something went wrong.'}
        />
      )}

      <form
        onSubmit={handleSubmit(async (data) => {
          await doAction({
            variables: {
              body: {
                phone_number: data.phone_number ?? '',
              },
            },
          });
        })}
      >
        <LayoutSpacerSmall>
          <Controller
            name={'phone_number'}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormInputPhoneNumber
                country_code={country?.data?.code}
                label={'Phone Number'}
                description={"If you already have an account, we'll log you in"}
                input_name={'phone_number'}
                error={errors.phone_number?.message}
                onChange={(value) =>
                  field.onChange({
                    target: {
                      value,
                    },
                  })
                }
              />
            )}
          />

          <FormSubmitButton label={'Submit'} loading={mutation_result.loading} />
        </LayoutSpacerSmall>
      </form>
    </LayoutSpacer>
  );
};
